import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { MantineProvider } from "@mantine/core";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { Userpilot } from "userpilot";
import { usePostHog } from "posthog-js/react";
import { AutoLogout } from "shared/src/components/AutoLogout";
import { AuthEvent } from "./data/auth-events";
import ProductLayout from "./components/Product/Layout/Layout";
import productRoutes from "./routes/productRoutes";
import productEligibilityRoutes from "./routes/productEligibilityRoutes";
import accountRoutes from "./routes/accountRoutes";
import routesWithoutSignup from "./routes/routesWithoutSignup";
import appealManagementRoutes from "./routes/appealManagementRoutes";
import QueryAuthGate from "./routes/queryAuthGate/QueryAuthGate";
import { ProductContextProvider } from "./contexts/ProductContext";
import { EligibilityProvider } from "./contexts/EligibilityContext";
import { AppealIdProvider } from "./contexts/AppealIdContext";
import { PatientProvider } from "./contexts/PatientContext";
import { RequireAppealId } from "./components/Shared/RequireAppealId";
import ClaimableAuthenticatorV2 from "./components/Shared/ClaimableAuthenticatorV2";
import Page from "./components/Product/Layout/Page";
import theme from "./theme";
import IdentifyUser from "./components/IdentifyUser";
import AuthRouteTracker from "./components/AuthRouteTracker";
import PathConstants from "./routes/pathConstants";
import { MetaProvider } from "./contexts/MetaContext";
import { InitialAppealProvider } from "./contexts/InitialAppealContext";
import AppealManagementAuthGate from "./routes/appealManagementAuthGate/AppealManagementAuthGate";

import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
});

Userpilot.initialize("NX-32bc6362");

const ResumeAppeal = React.lazy(
  () => import("./pages/Product/Resume/ResumeAppeal"),
);

const Appeals = React.lazy(() => import("./pages/Product/Account/Appeals"));
const ReportAppealResultPage = React.lazy(
  () => import("./pages/Product/ReportingFlow/ReportAppealResult"),
);
const UpdateAppealStatus = React.lazy(
  () => import("./pages/update-appeal-status"),
);
const MagicLinkUpheldFollowUp = React.lazy(
  () => import("./pages/magic-link-upheld-follow-up"),
);
function AutoLogoutWrapper() {
  const { signOut, authStatus } = useAuthenticator();
  const posthog = usePostHog();

  const handleLogout = () => {
    localStorage.clear();
    signOut();
  };

  const handleInactivityLogout = () => {
    posthog.capture(AuthEvent.InactivityLogout);
  };

  if (authStatus !== "authenticated") {
    return null;
  }

  return (
    <AutoLogout
      onLogout={handleLogout}
      onInactivityLogout={handleInactivityLogout}
    />
  );
}

function App() {
  const router = createBrowserRouter([
    {
      element: (
        <ClaimableAuthenticatorV2>
          <ProductContextProvider>
            <RequireAppealId>
              <ProductLayout />
            </RequireAppealId>
          </ProductContextProvider>
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productRoutes,
    },
    {
      element: (
        <ProductContextProvider>
          <ProductLayout />
        </ProductContextProvider>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productEligibilityRoutes,
    },
    {
      element: (
        <ClaimableAuthenticatorV2 defaultTab="signIn">
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: accountRoutes,
    },
    {
      element: (
        <ClaimableAuthenticatorV2 defaultTab="signIn">
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        {
          path: PathConstants.MagicLinkUpheldFollowUp,
          element: <MagicLinkUpheldFollowUp />,
        },
      ],
    },
    {
      element: (
        <ClaimableAuthenticatorV2 defaultTab="signIn" hideSignUp>
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: routesWithoutSignup,
    },
    {
      element: <AppealManagementAuthGate />,
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: appealManagementRoutes,
    },
    {
      element: (
        <ProductContextProvider>
          <ProductLayout />
        </ProductContextProvider>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        { path: PathConstants.ResumeAppeal, element: <ResumeAppeal /> },
        {
          path: PathConstants.Appeals,
          element: (
            <QueryAuthGate>
              <Appeals />
            </QueryAuthGate>
          ),
        },
        {
          path: PathConstants.ReportAppealResult,
          element: (
            <QueryAuthGate>
              <ReportAppealResultPage />
            </QueryAuthGate>
          ),
        },
      ],
    },
    {
      element: (
        <ProductContextProvider>
          <ProductLayout />
        </ProductContextProvider>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        {
          path: PathConstants.UpdateAppealStatus,
          element: <UpdateAppealStatus />,
        },
      ],
    },
  ]);

  return (
    <MantineProvider theme={theme}>
      <MetaProvider>
        <Authenticator.Provider>
          <AppealIdProvider>
            <PatientProvider>
              <InitialAppealProvider>
                <EligibilityProvider>
                  <RouterProvider router={router} />
                </EligibilityProvider>
              </InitialAppealProvider>
            </PatientProvider>
          </AppealIdProvider>
          <IdentifyUser />
          <AuthRouteTracker />
          <AutoLogoutWrapper />
        </Authenticator.Provider>
      </MetaProvider>
    </MantineProvider>
  );
}

export default App;
